import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Divider, Form } from 'antd'
import { useQuery } from 'react-query'
import { InputSelect, Modal } from '@lmc/ui-kit'
import styled from 'styled-components'

import { NeedCategoryDescriptionEnum } from '@lmc/validations/lib/schemas/v6/needs'
import {
  fetchAllNeeds,
  fetchNeedSuggestions,
  NeedItem,
  NEEDS_QUERY_KEYS,
  useNeedCreate,
} from 'hooks/useNeeds'

type AddNeedModalProps = {
  isModalVisible: boolean
  onClose: () => void
}

export const AddNeedModal: FC<AddNeedModalProps> = ({
  isModalVisible,
  onClose,
}) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()

  const handleCloseForm = () => {
    form.resetFields()
    onClose()
  }

  const { createNeed, isCreating } = useNeedCreate(handleCloseForm)

  const { data: suggestions = [] } = useQuery<NeedItem[]>(
    [NEEDS_QUERY_KEYS.suggestions],
    fetchNeedSuggestions,
    { retry: false, retryOnMount: false, enabled: isModalVisible }
  )

  const { data: existingNeeds = [] } = useQuery<NeedItem[]>(
    [NEEDS_QUERY_KEYS.options],
    fetchAllNeeds,
    { retry: false, retryOnMount: false, enabled: isModalVisible }
  )

  const existingNeedNames = existingNeeds.map((n) => n.name.toLowerCase())

  const needOptions = suggestions.map((need) => ({
    label: need.name,
    value: need.name,
    disabled: existingNeedNames.includes(need.name.toLowerCase()),
  }))

  const categoryOptions = Object.entries(NeedCategoryDescriptionEnum).map(
    ([code, description]) => ({ label: description, value: code })
  )

  const onConfirm = () => {
    form.submit()
  }

  const onFinish = async (values: {
    need: string | string[]
    modsCategory?: string
  }) => {
    const needName = Array.isArray(values.need) ? values.need[0] : values.need

    if (!needName?.trim()) {
      form.setFields([
        {
          name: 'need',
          errors: [t('skillsAndNeeds.form.needNameRequired')],
        },
      ])
      return
    }

    if (existingNeedNames.includes(needName.trim().toLowerCase())) {
      form.setFields([
        {
          name: 'need',
          errors: [t('skillsAndNeeds.form.needDuplicate')],
        },
      ])
      return
    }

    const payload: { name: string; modsCategory?: string } = {
      name: needName.trim(),
    }
    if (values.modsCategory) {
      payload.modsCategory = values.modsCategory
    }

    await createNeed(payload)
  }

  return (
    <StyledModal
      localisation={{
        title: t('skillsAndNeeds.addNeedModalTitle'),
        confirmText: t('skillsAndNeeds.addNeedConfirm'),
        cancelText: t('general.buttons.cancel'),
      }}
      onConfirm={onConfirm}
      onCancel={handleCloseForm}
      isModalVisible={isModalVisible}
      closeButton
      closeOnConfirm={false}
      noPadding
      confirmButtonProps={{ isLoading: isCreating, variant: 'primary' }}
    >
      <Divider style={{ margin: '0 0 16px 0' }} />
      <Form form={form} onFinish={onFinish}>
        <InputSelect
          testId="need-name-select"
          type="need"
          required
          label={t('skillsAndNeeds.form.needName')}
          helperText={t('skillsAndNeeds.form.needNameTooltip')}
          errorMessage={t('skillsAndNeeds.form.needNameRequired')}
          layout="column"
          placeholder={t('skillsAndNeeds.form.needNamePlaceholder')}
          options={needOptions}
          showSearch
          mode="tags"
          onSearch={(searchValue) => {
            const name = searchValue?.trim().toLowerCase()
            const isDuplicate = name && existingNeedNames.includes(name)
            form.setFields([
              {
                name: 'need',
                errors: isDuplicate
                  ? [t('skillsAndNeeds.form.needDuplicate')]
                  : [],
              },
            ])
          }}
          onChange={(value) => {
            const selected = value?.slice(-1)
            const name = selected?.[0]?.trim().toLowerCase()
            const isDuplicate = name && existingNeedNames.includes(name)
            form.setFields([
              {
                name: 'need',
                value: selected,
              },
            ])
            if (isDuplicate) {
              setTimeout(() => {
                form.setFields([
                  {
                    name: 'need',
                    errors: [t('skillsAndNeeds.form.needDuplicate')],
                  },
                ])
              }, 0)
            }
          }}
        />
        <InputSelect
          testId="need-category-select"
          type="modsCategory"
          required
          label={t('skillsAndNeeds.form.needCategoryLabel')}
          helperText={t('skillsAndNeeds.form.needCategoryTooltip')}
          errorMessage={t('skillsAndNeeds.form.needCategoryRequired')}
          layout="column"
          placeholder={t('skillsAndNeeds.form.needCategoryPlaceholder')}
          options={categoryOptions}
          showSearch
        />

      </Form>
      <Divider style={{ margin: '8px 0 0 0' }} />
    </StyledModal>
  )
}

const StyledModal = styled(Modal)`
  min-width: 600px;

  .ant-modal-content {
    padding-top: 20px !important;
    padding-left: 24px !important;
    padding-right: 24px !important;
  }

  .ant-form-item-label > label {
    font-weight: 500 !important;
    font-size: 15px !important;
  }

  .ant-form-item-label > label span {
    font-size: 15px !important;
  }

  .ant-form-item-explain-error {
    font-size: 15px !important;
  }

  .ant-select {
    height: 40px !important;
  }

  .ant-modal-header {
    padding-bottom: 4px !important;
  }

  .ant-modal-title {
    font-size: 22px !important;
    font-weight: 500 !important;
    text-transform: none !important;
    color: ${({ theme }) => theme.color.neutral.color3} !important;
  }

  .ant-modal-footer {
    margin-top: 0 !important;
    padding-top: 12px !important;
    padding-bottom: 12px !important;
  }
`

export default AddNeedModal
